import * as React from "react";
import {
	Box,
	Button,
	Img,
	Text,
	Tooltip,
	useMediaQuery,
} from "@chakra-ui/react";
import { AppDispatch, ERROR_MSG, SET_MESSAGE } from "../../packages";
import FileUploadAnimation from "../../img/FileUploadAnimation.gif";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";

export const FileUpload = (props: any) => {
	const dispatch = useDispatch<AppDispatch>();
	const [currentPlan] = useSelector((state: any) => [state.subscriptionPlan]);
	const [files, setFiles] = React.useState<File[]>([]);
	const [isMobileTablet] = useMediaQuery("(max-width: 768px)");

	const onDrop = React.useCallback(
		(acceptedFiles: any, fileRejections: any) => {
			let isLargeFile = 0,
				fileNotSupported = 0,
				invalidFileFormat = 0;

			// Check rejected files
			fileRejections.forEach((file: any) => {
				file.errors.forEach((err: any) => {
					if (err.code === "file-too-large") {
						isLargeFile++;
					}
					if (err.code === "file-invalid-type") {
						fileNotSupported++;
					}
				});
			});

			// Filter accepted files to remove unsupported formats
			acceptedFiles = acceptedFiles.filter((file: any) => {
				const fileExt = file.name?.split(".").pop()?.toLowerCase();
				if (fileExt !== "step" && fileExt !== "stp") {
					fileRejections.push(file);
					return false;
				}
				return true;
			});

			// Handle errors
			if (
				isLargeFile > 0 &&
				fileNotSupported === 0 &&
				invalidFileFormat === 0
			) {
				dispatch({
					type: SET_MESSAGE,
					payload: {
						case: ERROR_MSG,
						data:
							isLargeFile === 1
								? "Whoa, that's a heavyweight file! 🏋️ Please keep it under 100MB."
								: "Some files are too large! Please keep them under 100MB.",
					},
				});
			} else if (
				isLargeFile === 0 &&
				(fileNotSupported > 0 || fileRejections.length > 0)
			) {
				dispatch({
					type: SET_MESSAGE,
					payload: {
						case: ERROR_MSG,
						data:
							fileRejections?.length > 0
								? "Unsupported file format detected! Please upload .STEP or .STP files only."
								: "Dashnode Alert: Some files are not yet part of our revolution. Please ensure all uploaded files are .STEP or .STP.",
					},
				});
			} else if (
				isLargeFile > 0 &&
				(fileNotSupported > 0 || fileRejections.length > 0)
			) {
				dispatch({
					type: SET_MESSAGE,
					payload: {
						case: ERROR_MSG,
						data: "Some files are either too large (>100MB) or not in .STEP/.STP format.",
					},
				});
			}

			// Process valid files
			if (
				acceptedFiles.length >
				currentPlan?.allocated_file_uploads - currentPlan?.used_file_uploads
			) {
				dispatch({
					type: SET_MESSAGE,
					payload: {
						case: ERROR_MSG,
						data: `You can upload only ${
							currentPlan?.allocated_file_uploads -
							currentPlan?.used_file_uploads
						} files. Upgrade your plan now!`,
					},
				});
			} else if (acceptedFiles.length > 0) {
				// Use the new queued upload system
				if (props.useQueuedUpload) {
					props.handleQueuedFileUpload(acceptedFiles);
				} else {
					// Fallback to original upload method
					props.handleFileUpload({ fileData: acceptedFiles, demofile: false });
				}
				setFiles(acceptedFiles);
			}
		},
		[props],
	);

	const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	const {
		getRootProps,
		getInputProps,
		open,
		fileRejections,
		isDragActive,
		isDragAccept,
		isDragReject,
	} = useDropzone({
		onDrop,
		// Disable click and keydown behavior
		noClick: true,
		noKeyboard: true,
		multiple: true,
		accept: isMobile
			? undefined
			: {
					"model/step": [".stp", ".STP", ".step", ".STEP"], // STEP file-specific format
					"application/octet-stream": [".stp", ".STP", ".step", ".STEP"], // Generic binary files
					"application/STEP": [".stp", ".STP", ".step", ".STEP"],
				},
		minSize: 10,
		maxSize: 52428799,
		disabled:
			currentPlan?.plan_status === "Expired" ||
			currentPlan?.used_file_uploads === currentPlan?.allocated_file_uploads,
	});

	return (
		<Box
			id="file-upload-area"
			className="file-upload-area"
			display="flex"
			flexDir="column"
			justifyContent="center"
			alignItems="center"
			py={props?.isNewUser ? "1.5rem" : "4.25rem"}
			w="100%"
			bg="#C5E9FF4D"
			border="1px dashed #0C64E8"
			gap="0.5rem"
			borderRadius="lg"
			{...getRootProps()}
		>
			<Img src={FileUploadAnimation} alt="file-upload-animation" />
			<Text onClick={open} textAlign="center">
				<span style={{ fontWeight: "500" }}>Drag and drop</span> files to
				upload, or
			</Text>
			<Tooltip
				label={
					currentPlan?.plan_status === "Expired" ||
					currentPlan?.used_file_uploads === currentPlan?.allocated_file_uploads
						? "Your Limit Exceeded/Expired, Please Upgrade Now!"
						: ""
				}
			>
				<Button
					className="aceFileUploadInitiateBtn"
					id="ace_file_upload_initiate_btn"
					fontWeight={"600"}
					color="#fff"
					w={isMobileTablet ? "10rem" : "14.4375rem"}
					bgColor="#0C64E8"
					_hover={{ bg: "#0087EE" }}
					onClick={open}
					isDisabled={
						currentPlan?.plan_status === "Expired" ||
						currentPlan?.used_file_uploads ===
							currentPlan?.allocated_file_uploads
					}
				>
					{props?.isNewUser ? "Upload your first file" : "Upload new file"}
				</Button>
			</Tooltip>
			<input {...getInputProps({})} type="file" />
			<Text color="#00000070" fontSize=".75rem" textAlign="center">
				Supports STEP & STP files under 50 MB. <br />
				Your data is always&nbsp;
				<span
					style={{
						color: "#0087ee",
						textDecoration: "underline",
						cursor: "pointer",
					}}
					onClick={() =>
						window.open("https://www.dashnode.ai/privacy-policy", "_blank")
					}
				>
					secure
				</span>
			</Text>
		</Box>
	);
};
